import React, { createContext, useContext, useState, useEffect, ReactNode, Suspense } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
// Import statique des traductions françaises et anglaises pour les avoir dès le début
import frTranslationsStatic from '../i18n/fr.json';
import enTranslationsStatic from '../i18n/en.json';

// Types pour les langues
export type Language = 'fr' | 'en';
type Translations = Record<string, any>;

// URL de base du site pour les URLs absolues
const BASE_URL = 'https://www.pokemonpocket.fr'; // Remplacer par l'URL réelle du site

// Interface pour le contexte
interface I18nContextType {
  lang: Language;
  setLang: (lang: Language) => void;
  t: (key: string, params?: Record<string, any>) => string;
  getPathForLang: (path: string, targetLang?: Language) => string;
  currentLang: Language;
  getAbsoluteUrlForLang: (lang: Language, path: string) => string;
  isLoading: boolean; // Ajout d'un indicateur de chargement
}

// Valeurs par défaut
const defaultContext: I18nContextType = {
  lang: 'fr',
  setLang: () => {},
  t: () => '',
  getPathForLang: () => '',
  currentLang: 'fr',
  getAbsoluteUrlForLang: () => '',
  isLoading: true, // Par défaut, on est en chargement
};

// Création du contexte
const I18nContext = createContext<I18nContextType>(defaultContext);

// Hook pour utiliser le contexte d'i18n
export const useI18n = () => useContext(I18nContext);

// Propriétés du provider
interface I18nProviderProps {
  children: ReactNode;
}

// Cache pour stocker les traductions déjà chargées
const translationsCache: Record<Language, Translations> = {
  fr: frTranslationsStatic, // Utiliser directement les traductions importées statiquement
  en: enTranslationsStatic, // Utiliser directement les traductions anglaises importées statiquement
};

// Fonction pour charger les traductions
const loadTranslations = async (lang: Language): Promise<Translations> => {
  try {
    // Si déjà dans le cache et non vide, utiliser le cache
    if (Object.keys(translationsCache[lang]).length > 0) {
      return translationsCache[lang];
    }
    
    // Pour le français et l'anglais, utiliser les traductions préchargées
    if (lang === 'fr') {
      return frTranslationsStatic;
    } else if (lang === 'en') {
      return enTranslationsStatic;
    }
    
    // Cette partie ne devrait pas être atteinte car nous n'avons que fr et en,
    // mais nous la gardons pour une extensibilité future
    const translations = (await import(`../i18n/${lang}.json`)).default;
    translationsCache[lang] = translations;
    return translations;
  } catch (error) {
    console.error(`Erreur lors du chargement des traductions pour ${lang}:`, error);
    return {};
  }
};

// Composant d'attente pendant le chargement initial
const I18nLoading = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
  </div>
);

// Provider du contexte
export const I18nProvider: React.FC<I18nProviderProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Fonction pour détecter la langue à partir de l'URL
  const detectLanguageFromUrl = (): Language => {
    const path = location.pathname;
    if (path.startsWith('/en/') || path === '/en') {
      return 'en';
    }
    return 'fr'; // Langue par défaut
  };
  
  // État pour stocker la langue actuelle
  const [lang, setLangState] = useState<Language>(detectLanguageFromUrl);
  
  // État pour stocker les traductions
  const [translations, setTranslations] = useState<Translations>(() => {
    // Utiliser directement les traductions statiques préchargées selon la langue
    const detectedLang = detectLanguageFromUrl();
    return detectedLang === 'fr' ? frTranslationsStatic : enTranslationsStatic;
  });
  
  // État pour indiquer si les traductions sont en cours de chargement
  // Comme les deux langues sont préchargées, on peut initialiser à false
  const [isLoading, setIsLoading] = useState<boolean>(false);
  
  // Charger les traductions lors du changement de langue
  useEffect(() => {
    const loadTranslationsForLang = async () => {
      // Ne mettre en chargement que si on n'a pas déjà les traductions
      if (Object.keys(translations).length === 0) {
        setIsLoading(true);
      }
      
      const newTranslations = await loadTranslations(lang);
      setTranslations(newTranslations);
      setIsLoading(false);
    };
    
    // Comme les deux langues sont préchargées, on peut simplement mettre à jour les traductions
    if (lang === 'fr') {
      setTranslations(frTranslationsStatic);
    } else if (lang === 'en') {
      setTranslations(enTranslationsStatic);
    } else {
      // Ce cas ne devrait pas se produire car nous n'avons que fr et en,
      // mais nous le gardons pour une extensibilité future
      loadTranslationsForLang();
    }
    
    setIsLoading(false);
  }, [lang]);
  
  // Observer les changements d'URL pour mettre à jour la langue si nécessaire
  useEffect(() => {
    const detectedLang = detectLanguageFromUrl();
    if (detectedLang !== lang) {
      setLangState(detectedLang);
    }
  }, [location.pathname, lang]);
  
  // Fonction pour changer de langue et mettre à jour l'URL
  const setLang = (newLang: Language) => {
    if (newLang === lang) return;
    
    setLangState(newLang);
    
    // Mettre à jour l'URL en fonction de la nouvelle langue
    const newPath = getPathForLang(location.pathname, newLang);
    navigate(newPath);
  };
  
  // Fonction pour obtenir une traduction par sa clé
  const t = (key: string, params?: Record<string, any>): string => {
    const keys = key.split('.');
    let value: any = translations;
    
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // En état de chargement, retourner une chaîne vide au lieu de la clé
        return isLoading ? '' : key;
      }
    }
    
    let result = typeof value === 'string' ? value : (isLoading ? '' : key);
    
    // Remplacer les paramètres si présents
    if (params && typeof result === 'string') {
      Object.entries(params).forEach(([paramKey, paramValue]) => {
        result = result.replace(new RegExp(`{{\\s*${paramKey}\\s*}}`, 'g'), String(paramValue));
      });
    }
    
    return result;
  };
  
  // Fonction pour convertir un chemin dans une langue spécifique
  const getPathForLang = (path: string, targetLang: Language = lang): string => {
    // Normaliser le chemin d'abord (enlever le préfixe /en/ s'il existe)
    const normalizedPath = path.replace(/^\/en\//, '/').replace(/^\/en$/, '/');
    
    // Si la langue cible est l'anglais, ajouter le préfixe /en
    if (targetLang === 'en') {
      // Éviter les doubles slash
      return normalizedPath === '/' ? '/en' : `/en${normalizedPath}`;
    }
    
    // Si la langue cible est le français, utiliser le chemin normalisé
    return normalizedPath;
  };
  
  // Fonction pour obtenir une URL absolue pour une langue spécifique
  const getAbsoluteUrlForLang = (targetLang: Language, path: string): string => {
    const localPath = getPathForLang(path, targetLang);
    return `${BASE_URL}${localPath}`;
  };
  
  const currentLang = lang;
  
  const contextValue: I18nContextType = {
    lang,
    setLang,
    t,
    getPathForLang,
    currentLang,
    getAbsoluteUrlForLang,
    isLoading,
  };
  
  return (
    <I18nContext.Provider value={contextValue}>
      {children}
    </I18nContext.Provider>
  );
}; 